"use client";

import { SessionProvider } from "next-auth/react";
import dynamic from "next/dynamic";
import { PostHogProvider } from "~/modules/analytics/components/PostHogProvider";
import { ReduxProvider } from "~/modules/app/components/ReduxProvider";
import { TRPCReactProvider } from "~/modules/app/components/TRPCReactProvider";
import { InlineAuthProvider } from "~/modules/auth/components/InlineAuthProvider";
import { ToasterLayout } from "~/modules/toaster/components/ToasterLayout";
import { GlobalRegistrationModal } from "~/modules/auth/components/GlobalRegistrationModal";
import { GlobalChatDockLayout } from "~/modules/asset_chat/components/GlobalChatDock/GlobalChatDockLayout";
import { UserLoader } from "~/modules/user/components/UserLoader";
import { ConsoleMessage, IntercomScript } from "~/components/Head";
import { SearchInitializer } from "~/modules/search/components/SearchInitializer";
import { SolanaWalletProvider } from "~/modules/swap";
import { ThemeProvider } from "~/components/ThemeProvider";
import { AuthRedirectManager } from "~/modules/auth/components/AuthRedirectManager";

import type { JSX } from "react";

// // // //

const SpeedInsights = dynamic(
    () => import("@vercel/speed-insights/next").then((m) => m.SpeedInsights),
    { ssr: false },
);

/**
 * Providers
 * The App Router counterpart to the provider stack in `src/pages/_app.tsx`.
 *
 * NOTE - the nesting order is load-bearing; keep the two files in sync until
 * the Pages Router is deleted.
 *
 * NOTE - `SessionProvider` intentionally receives no `session` prop. `_app`
 * passes `pageProps.session`, but no page populates it today, so this is a
 * no-op difference.
 */
export function Providers(props: { children: React.ReactNode }): JSX.Element {
    const { children } = props;

    return (
        <>
            {/* Load console message */}
            <ConsoleMessage />

            <ThemeProvider
                attribute="class"
                defaultTheme="light"
                enableSystem
                disableTransitionOnChange
            >
                <SessionProvider>
                    <PostHogProvider>
                        <ReduxProvider>
                            {/* NOTE - TRPCReactProvider must wrap everything that uses tRPC or react-query hooks, */}
                            {/* including the global layouts below (i.e. GlobalChatDockLayout) */}
                            <TRPCReactProvider>
                                <SolanaWalletProvider>
                                    <SearchInitializer>
                                        {/* NOTE - we must keep UserLoader here because its */}
                                        {/* responsible for firing off the user authentication handshake */}
                                        <UserLoader>
                                            <>
                                                {/* Load Vercel Speed Insights */}
                                                <SpeedInsights />

                                                {/* Load Intercom */}
                                                <IntercomScript />

                                                {/* Inline auth provider renders authentication and onboarding modals that manage their own visibility */}
                                                <InlineAuthProvider>
                                                    {children}
                                                </InlineAuthProvider>

                                                {/* Handle post-authentication redirect */}
                                                <AuthRedirectManager />
                                            </>
                                        </UserLoader>
                                    </SearchInitializer>
                                </SolanaWalletProvider>

                                <ToasterLayout />
                                <GlobalRegistrationModal />
                                <GlobalChatDockLayout />
                            </TRPCReactProvider>
                        </ReduxProvider>
                    </PostHogProvider>
                </SessionProvider>
            </ThemeProvider>
        </>
    );
}
